import { useCallback } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import type { Report } from "../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../contexts/ApiClientProviderContext";
import { useAppDispatch } from "../../store/hooks/useAppDispatch";
import { setEditReport } from "../../store/thunks/currentReportThunks";
import Visualization from "../common/visualization/Visualization";
import useReportTypeChange from "./common/hooks/useReportTypeChange";

interface Props {
  report: Report;
}

const ReportTypeSelector = ({ report }: Props) => {
  const { updateReport } = useEnhancedBiApiClientProvider();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { onReportTypeChanged } = useReportTypeChange();
  const dispatch = useAppDispatch();

  const update = useCallback(async (report: Report): Promise<Report | undefined> => {
    try {
      const response = await updateReport(report);
      if (response.success) {
        sendNotification("The report was successfully saved.");
        dispatch(setEditReport(response.data));

        return response.data;
      }
      sendNotificationError("An error occurred while saving the report.");

      return undefined;
    } catch (error) {
      sendNotificationError("An error occurred while saving the report.");
      logError(error, "[ReportTypeSelector] update");

      return undefined;
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  return (
    <Visualization report={report} onSelect={onReportTypeChanged} update={update}>
      <Visualization.Label />
      <Visualization.ReportTypesContainer />
      <Visualization.UnsavedChangesPopover reportKind="report" />
      <Visualization.SaveReportDialog />
    </Visualization>
  );
};

export default ReportTypeSelector;
